<template>
    <div v-loading="loading" align="center">
        <div v-if="authenticated" class="main-body">
            <div class="home-header">
                <div v-if="isPlayerOrCoach()">
                    <div v-if="!checksCompletedAndValid" class="welcome-message red">
                        <h3>Welcome, {{ user.displayName }}!</h3>
                        <p class="error-message">Please complete the requirements below to access the Player Zone</p>
                    </div>

                    <div v-else class="welcome-message green">
                        <h3>Welcome, {{ user.displayName }}!</h3>
                        <a href="#" @click="openPlayerZone()">Go to Player Zone</a>
                    </div>
                </div>
                <div v-else-if="isAgent()" class="welcome-message green">
                    <h3>Welcome, {{ user.displayName }}!</h3>
                    <a href="#" @click="openPlayerZoneasAgent()">Go to Player Zone as Agent</a>
                </div>
                <div v-else class="welcome-message green">
                    <h3>Welcome, {{ user.displayName }}!</h3>
                    <a href="#" @click="openPlayerZone()">Go to Player Zone</a>
                </div>
            </div>

            <div class="profile-card" v-if="isPlayerOrCoach() && isCoachValidToAccess">
                <StatusCard message="Payment Status" :success="isPaymentDue() === false" linkText="Make Payment" linkUrl="/player-payment" />
            </div>

            <div class="profile-card" v-if="isPlayer()">
                <StatusCard message="ITIA Training" :success="isTiuStatusValid() === true" linkText="Complete Training" :linkUrl="user.tiuEnvelopeUrl" />
            </div>

            <div class="profile-card" v-if="isPlayer()">
                <StatusCard
                    :message="loadingPlayerAnnualConsentFormError || 'Player Annual Form'"
                    :success="(!!hasThePlayerAnnualConsentFormBeenSigned && !loadingPlayerAnnualConsentFormError) || false"
                    :linkText="loadingPlayerAnnualConsentFormError ? '' : 'Sign Form'"
                    :linkUrl="loadingPlayerAnnualConsentFormError ? '' : playerAnnualFormSharepointUrl"
                />
            </div>

            <div class="profile-card" v-if="isPlayer()">
                <HipaaFormValidation :player-email="user.email" :player-i-d="user.employeeId" @is-hipaa-form-valid="setIsHipaaFormValid" />
            </div>

            <div v-if="isAgent() && checksCompletedAndValid">
                <div class="agent-message">
                    <h4>As an agent you can also act for the players associated to you</h4>
                    Please select the player below and click on the button “Act for player and open Player Zone”. You will then be granted access to all Player
                    Zone functions available to that player. If you are missing player entries to select, then please contact the WTA. Your email you are using
                    for login ({{ user.email }}) might not be assigned to the player.

                    <div v-if="playerSelectedForImpersonation && !impersonatedChecksCompletedAndValid" class="welcome-message red">
                        <p class="error-message">The player selected has pending requirements to access the Player Zone.</p>
                    </div>

                    <div class="mt-8">
                        <el-select v-model="playerSelectedForImpersonation">
                            <el-option
                                v-for="player in playersAssociatedToAgent"
                                :key="player.id"
                                :label="player.lastName + ', ' + player.firstName"
                                :value="player.id"
                            ></el-option>
                        </el-select>
                        <el-button v-if="playerSelectedForImpersonation" class="link-button ml-8" @click="impersonatePlayerForAgent(false)"
                            >Act for Player and open Player Zone</el-button
                        >
                        <el-button v-if="playerSelectedForImpersonation" class="link-button ml-8" @click="impersonatePlayerForAgent(true)"
                            >Perform Payment for Player</el-button
                        >
                    </div>
                </div>
            </div>

            <div v-if="isCoach() && !isCoachValidToAccess">
                <div class="agent-message">
                    <p class="text-small">
                        You need to have an employee ID to access the system. Please create a ticket to the WTA to get one:
                    </p>
                    <a :href="ticketFormUrl" target="_blank" rel="noopener noreferrer" class="request-link">
                        Create Request
                    </a>
                </div>
            </div>
        </div>

        <div class="border p-16 mt-15" v-if="authenticated && (mode === 'uat' || mode === 'development')" style="opacity: 0.7">
            The following functionality is only visible in UAT and Development. It's purpose is to provide quick access to functionalities that Players, Agent
            and Coaches get through links in Player Zone Sharepoint Site.
            <template>
                <br /><br />Set another player identifier <input type="text" v-model="overwrittenPlayerIdentifier" />
                <input type="submit" @click="setPlayerIdentifier" value="Save" /><input
                    type="submit"
                    @click="clearPlayerIdentifier"
                    value="Reset to Original"
                />
                for this session only.
            </template>
            <div class="mt-16">
                <code>
                    {{ user.displayName }} PY: {{ paymentInformation.plyrNum }} R: {{ user.role }}-{{ paymentInformation.role }}, E:
                    {{ paymentInformation.employeeId }}, OV: {{ overwrittenPlayerIdentifier }}, M: {{ mode }}, WM: {{ paymentInformation.witaMember }} PD:
                    {{ paymentInformation.paymentDate }} OM: {{ paymentInformation.owesMoney }} US: {{ paymentInformation.username }}
                </code>
            </div>
            <el-row :gutter="10" class="mt-32">
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Profile and Ranking Summaries</b>
                        </div>
                        <div class="text-small card-text">
                            This app allows you to see all information we have stored on your account. Also it provides information about your ranking details
                            and other players ranking summaries.
                        </div>
                        <el-button @click="openProfile" class="link-button">Open </el-button>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Membership Payment and Subscription</b>
                        </div>
                        <div class="text-small card-text">
                            Your membership payments and subscriptions can be managed through here.
                        </div>
                        <el-button @click="openPayment" class="link-button">Open</el-button>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Singles and Doubles Entries</b>
                        </div>
                        <div class="text-small card-text">
                            This page allows you to enter Singles and Doubles tournaments. In addition to that you can withdraw entries and see the history.
                        </div>
                        <el-button @click="openEntry" class="link-button">
                            Open
                        </el-button>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Acceptance and Preliminary Lists</b>
                        </div>
                        <div class="text-small card-text">
                            This app enables you to see, for any Singles or Doubles tournament, the preliminary list and the acceptance.
                        </div>
                        <el-button @click="openAcceptance" class="link-button">Open</el-button>
                    </el-card>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script>
import * as axios from 'axios';
import LogRocket, { error } from 'logrocket';
import { mapGetters } from 'vuex';
import HipaaFormValidation from './HipaaFormValidation.vue';
import StatusCard from './StatusCard.vue';
import moment from 'moment';

export default {
    components: { HipaaFormValidation, StatusCard },
    data() {
        return {
            mode: process.env.VUE_APP_APPLICATION_MODE,
            url: process.env.VUE_APP_API_URL,
            version: null,
            loading: true,
            loadingAuth: false,
            user: {
                displayName: '',
                employeeId: '',
                roles: [],
                tiuVerified: false,
                tiuEnvelopeUrl: '',
                email: '',
            },
            isCoachValidToAccess: false,
            paymentInformation: {},
            authenticated: false,
            overwrittenPlayerIdentifier: '',
            playersAssociatedToAgent: [],
            tripwireStatuses: {},
            impersonatedChecksCompletedAndValid: true,
            playerSelectedForImpersonation: '',
            annualFormValidation: {},
            hipaaAnnualFormValidation: false,
            codesFromPlayerAnnualFormsToAble: [
                'PlayerConsentForm.PlayerAlreadySignedForm',
                'PlayerConsentForm.PlayerNotIsEnableToSign',
                'PlayerConsentForm.PlayerNotOfLegalAge',
            ],
            ticketFormUrl: process.env.VUE_APP_REQUEST_TICKET_URL,
            annualOldDate: null,
            annualNewDate: null,
            playerAnnualFormSharepointUrl: null,
        };
    },
    created() {
        this.loadingAuth = true;
        localStorage.setItem('tripwireStatus', 'false');

        this.$msal.authenticateRedirect().then((data) => {
            this.$msal.getSilentToken(data[0], ['User.Read']).then((token) => {
                console.log('Authentication flow triggered: After silent token');
                this.$msal
                    .callMSGraph('https://graph.microsoft.com/v1.0/me?$select=displayName,givenName,employeeId', token.accessToken)
                    .then((graphResponse) => {
                        graphResponse.json().then((jsonResponse) => {
                            this.setupEnvironment(token, jsonResponse);
                            localStorage.setItem('jwt', token.idToken);
                            localStorage.setItem('accessToken', token.accessToken);
                            this.loadFormsStartDates();
                            if (!this.isPlayerOrCoach() && !this.isAgent()) {
                                this.openWtaZone();
                            } else {
                                this.authenticated = true;
                                if (this.isAgent()) {
                                    localStorage.setItem('tripwireStatus', 'true');
                                    this.loadAgentImpersonationData();
                                } else {
                                    if (this.isCoach()) {
                                        if (this.user.employeeId === null || this.user.employeeId === '') {
                                            this.isCoachValidToAccess = false;
                                            this.authenticated = true;
                                            this.$message({
                                                showClose: true,
                                                message: 'You need to have an employee ID to access the system. Please create a ticket to the WTA to get one.',
                                                type: 'error',
                                                duration: 2500,
                                            });
                                            this.loading = false;
                                            this.loadingAuth = false;
                                        } else {
                                            this.isCoachValidToAccess = true;
                                            this.loadPaymentAndStatusInformation();
                                            this.loadAnnualFormsValidation();
                                        }
                                    } else {
                                        this.loadPaymentAndStatusInformation();
                                        this.loadAnnualFormsValidation();
                                        this.loading = false;
                                        this.loadingAuth = false;
                                        this.isCoachValidToAccess = true;
                                    }
                                }
                            }
                        });
                    });
            });
        });
    },
    computed: {
        checksCompletedAndValid() {
            if (this.isCoach()) {
                return this.isCoachValidToAccess && !this.isPaymentDue();
            }

            if (this.isPlayerOrCoach()) {
                let tiuValid = true;

                if (this.isPlayer()) {
                    tiuValid = this.isTiuStatusValid();
                }

                let isHipaaFormValidToContinue = this.isHipaaFormValidToContinue();
                let isAnnualFormValidToContinue = this.isAnnualFormValidToContinue();

                if (this.isPlayerUnder18()) {
                    isAnnualFormValidToContinue = true;
                    isHipaaFormValidToContinue = true;
                }

                return !this.isPaymentDue() && tiuValid && isAnnualFormValidToContinue && isHipaaFormValidToContinue;
            }

            return true;
        },
        ...mapGetters('information', ['isUserEnableToSignTheAnnualForm', 'hasThePlayerAnnualConsentFormBeenSigned', 'loadingPlayerAnnualConsentFormError']),
        ...mapGetters('playerConsentFormStore', ['hasTheHipaaConsentFormBeenSigned']),
    },
    methods: {
        isHipaaFormValidToContinue() {
            if (!this.isPlayer()) {
                return true;
            }
            return this.hasTheHipaaConsentFormBeenSigned;
        },
        isAnnualFormValidToContinue() {
            return this.hasThePlayerAnnualConsentFormBeenSigned;
        },
        setIsHipaaFormValid(value) {
            this.hipaaAnnualFormValidation = this.isPlayer() ? value : true;
        },
        setupEnvironment(token, jsonResponse) {
            const d = false;
            if (!d) {
                this.$set(this.user, 'roles', token.idTokenClaims.roles);
                this.$set(this.user, 'displayName', jsonResponse.displayName);
                const employeeIdFromAd = jsonResponse.employeeId;
                this.$set(this.user, 'employeeId', employeeIdFromAd);
                this.$set(this.user, 'email', token.idTokenClaims.preferred_username);
                if (localStorage.getItem('overwrittenPlayerIdentifier') !== null) {
                    this.$set(this.user, 'employeeId', localStorage.getItem('overwrittenPlayerIdentifier'));
                } else {
                    this.$set(this.user, 'employeeId', employeeIdFromAd);
                    window.playerIdentifier = employeeIdFromAd;
                }
                LogRocket.identify(employeeIdFromAd, {
                    name: jsonResponse.displayName,
                    email: token.idTokenClaims.preferred_username,
                    roleType: token.idTokenClaims.roles,
                    overwrittenType: localStorage.getItem('overwrittenPlayerIdentifier') !== null,
                });
            } else {
                this.$set(this.user, 'roles', ['']);
                this.$set(this.user, 'displayName', 'xxx');
                this.$set(this.user, 'email', 'xxx');
                const employeeIdFromAd = '';
                this.$set(this.user, 'employeeId', employeeIdFromAd);
                window.playerIdentifier = employeeIdFromAd;
            }
        },
        async impersonatePlayerForAgent(showPayment) {
            if (this.playerSelectedForImpersonation) {
                if (showPayment) {
                    localStorage.setItem('overwrittenPlayerIdentifier', this.playerSelectedForImpersonation);
                    this.openPayment();
                } else {
                    this.loading = true;
                    await this.loadTripwireValidations();

                    this.loading = true;
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                    this.loading = false;

                    this.impersonatedChecksCompletedAndValid = this.tripwireStatuses.allChecksValid;

                    if (this.impersonatedChecksCompletedAndValid) {
                        localStorage.setItem('overwrittenPlayerIdentifier', this.playerSelectedForImpersonation);
                        this.openPlayerZone();
                    } else {
                        return;
                    }
                }
            }
        },
        async loadTripwireValidations() {
            await this.$store.dispatch('information/retrieveTripwireValidations', { playerNumber: this.playerSelectedForImpersonation }).then((data) => {
                this.loading = false;
                this.loadingAuth = false;
                this.authenticated = true;
                this.tripwireStatuses = data;
            });
        },
        loadAgentImpersonationData() {
            this.$store.dispatch('information/retrievePlayersAssociatedToAgent', { userEmail: this.user.email }).then((data) => {
                this.loading = false;
                this.loadingAuth = false;
                this.authenticated = true;
                this.playersAssociatedToAgent = data;
            });
        },
        loadFormsStartDates() {
          this.$store.dispatch('information/retrieveFormsStartDates').then(([annualOldDate, annualNewDate]) => {
            this.loading = false;
            this.loadingAuth = false;
            this.authenticated = true;
            this.annualOldDate = annualOldDate;
            this.annualNewDate = annualNewDate;

            if(moment().format('YYYY-MM-DD') >= moment(this.annualNewDate).format('YYYY-MM-DD')) {
              this.playerAnnualFormSharepointUrl = process.env.VUE_APP_PLAYER_ANNUAL_FORMS_CURRENT_YEAR;
            } else if(
                moment().format('YYYY-MM-DD') >= moment(this.annualOldDate).format('YYYY-MM-DD') &&
                moment().format('YYYY-MM-DD') < moment(this.annualNewDate).format('YYYY-MM-DD')
            ) {
              this.playerAnnualFormSharepointUrl = process.env.VUE_APP_PLAYER_ANNUAL_FORMS_PREVIOUS_YEAR
            }

          });
        },
        loadPaymentAndStatusInformation() {
            this.$store
                .dispatch('information/retrievePaymentInformation', { playerIdentifier: this.user.employeeId })
                .then((data) => {
                    this.paymentInformation = data;
                    this.$store
                        .dispatch('information/retrieveTennisIntegrityUnitStatus', { playerIdentifier: this.user.employeeId })
                        .then((tiuData) => {
                            if (tiuData.testVerified !== true) {
                                this.user.tiuVerified = false;
                                this.user.tiuEnvelopeUrl = tiuData.encryptedEnvelopeUrl;
                            } else {
                                this.user.tiuVerified = true;
                            }
                            this.loading = false;
                            this.loadingAuth = false;
                            this.authenticated = true;
                            this.playersAssociatedToAgent = data;

                            // TO DO: This is atemporary solution to validate the status of the tripwire in the different microsites
                            localStorage.setItem('tripwireStatus', this.checksCompletedAndValid ? 'true' : 'false');

                            if (this.checksCompletedAndValid && this.mode === 'production') {
                                setTimeout(() => this.openPlayerZone(), 250);
                            }
                        })
                        .catch((e) => {
                            console.error('Error from TIU: ', { e });
                            this.loading = false;
                            this.loadingAuth = false;
                            this.$message({
                                showClose: true,
                                message: 'Could not retrieve information from WTA backend systems. Your database entry seems to be erroneous.',
                                type: 'error',
                                duration: 0,
                            });
                        });
                })
                .catch((e) => {
                    this.loading = false;
                    this.loadingAuth = false;
                    this.$message({
                        showClose: true,
                        message:
                            'Could not retrieve information from WTA backend systems. ' +
                            'Retrying in 5 seconds. If the problem persists then contact the Administrator',
                        type: 'error',
                        duration: 0,
                    });
                    window.setTimeout(() => {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/';
                    }, 5000);
                });
        },
        loadAnnualFormsValidation() {
            this.$store
                .dispatch('information/retrieveAnnualFormValidation', { playerIdentifier: this.user.employeeId, playerEmail: this.user.email })
                .then((data) => {
                    this.annualFormValidation = data;
                })
                .catch((e) => {
                    this.loading = false;
                    this.loadingAuth = false;
                    this.$message({
                        showClose: true,
                        message:
                            'Could not retrieve information from WTA backend systems. ' +
                            'Retrying in 5 seconds. If the problem persists then contact the Administrator',
                        type: 'error',
                        duration: 0,
                    });
                    window.setTimeout(() => {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/';
                    }, 5000);
                });
        },

        isPaymentDue() {
            return this.paymentInformation.paymentRequiredNow;
        },
        isTiuStatusValid() {
            return this.user.tiuVerified;
        },
        isPlayerOrCoach() {
            return this.user.roles !== undefined && (this.user.roles.indexOf('Players') >= 0 || this.user.roles.indexOf('Coaches') >= 0);
        },

        isPlayer() {
            return this.user.roles !== undefined && this.user.roles.indexOf('Players') >= 0;
        },

        isPlayerUnder18() {
            return (this.loadingPlayerAnnualConsentFormError && this.loadingPlayerAnnualConsentFormError.includes('18')) || false;
        },

        isCoach() {
            return this.user.roles !== undefined && this.user.roles.indexOf('Coaches') >= 0;
        },

        isAgent() {
            return this.user.roles !== undefined && this.user.roles.indexOf('Agent') >= 0;
        },

        openAcceptance() {
            window.location.href = '/prelim-and-al';
            return true;
        },

        openEntry() {
            window.location.href = '/player-entries';
            return true;
        },

        openPayment() {
            window.location.href = '/player-payment';
            return true;
        },

        openProfile() {
            window.location.href = '/player-profile';
            return true;
        },

        openWtaZone() {
            window.location.href = 'https://wtatennis.sharepoint.com/sites/wta-zone';
            return true;
        },

        openPlayerZone() {
            window.location.href = process.env.VUE_APP_URL_HOME;
            return true;
        },

        openPlayerZoneasAgent() {
            localStorage.removeItem('overwrittenPlayerIdentifier');
            window.location.href = process.env.VUE_APP_URL_HOME;
            return true;
        },

        setPlayerIdentifier() {
            this.user.employeeId = this.overwrittenPlayerIdentifier;
            this.loading = true;
            localStorage['overwrittenPlayerIdentifier'] = this.overwrittenPlayerIdentifier;
            this.loadPaymentAndStatusInformation();
        },

        clearPlayerIdentifier() {
            this.loading = true;
            localStorage.removeItem('overwrittenPlayerIdentifier');
            window.location.reload();
        },
    },
};
</script>

<style>
.main-body {
    margin-bottom: 80px;
}

.home-header {
    margin-bottom: 25px;
}

.welcome-message {
    text-align: center;
    padding: 20px;
    border-radius: 1px;
    margin: 0 auto;
    max-width: 500px;
    font-family: Arial, sans-serif;
    font-size: 16px;
    color: #1a1a1a;
    background-color: #ffffff;
    margin-top: 20px;

    &.green {
        border: 1px solid #4c9141;
    }

    &.red {
        border: 2px solid #a60000;
    }

    h3 {
        margin: 0 0 25px;
        font-size: 20px;
        font-weight: bold;
    }

    a {
        text-decoration: underline;
        font-size: 15px;
        font-weight: 600;
        color: #1a1a1a;
    }

    .error-message {
        margin-top: 10px;
        font-size: 15px;
        font-weight: 600;
    }
}

.agent-message {
    text-align: center;
    padding: 20px;
    border-radius: 5px;
    margin: 0 auto;
    max-width: 800px;
    font-family: Arial, sans-serif;
    font-size: 13px;
    color: #1a1a1a;
    margin-top: 30px;
    margin-bottom: 40px;
    background-color: #f9f9f9;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    h4 {
        margin: 0;
        padding: 0;
        margin-bottom: 15px;
    }
}

.profile-card {
    margin-bottom: 15px;
}

.el-container {
    background-color: #f5f5f5;
}
</style>
